// Therapist — Recurring weekly timetable template.
//
// One template per therapist. Edits apply immediately and seed each
// new week's daysheet the first time the therapist opens it. Manual
// edits to already-materialized weeks are preserved.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Loader2, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const empty = () => ({
  day_of_week: 0, time: '09:00', duration_minutes: 30,
  child_name: '', child_id: '', session_type: 'individual', cpt_code: '', notes: '',
});

export default function TherapistTimetable() {
  const [entries, setEntries] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    (async () => {
      try {
        const [t, c] = await Promise.all([
          axios.get(`${API}/timetable/template`, auth()),
          axios.get(`${API}/children`, auth()),
        ]);
        setEntries(t.data?.entries || []);
        setChildren(c.data || []);
      } catch (e) {
        toast.error('Failed to load timetable');
      } finally { setLoading(false); }
    })();
  }, []);

  const addEntry = () => setEntries((es) => [...es, { ...empty() }]);
  const removeEntry = (i) => setEntries((es) => es.filter((_, idx) => idx !== i));
  const updateEntry = (i, patch) =>
    setEntries((es) => es.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));

  const save = async () => {
    setSaving(true);
    try {
      // strip fields we don't want to send
      const payload = {
        entries: entries.map(({ id, ...rest }) => ({
          ...rest,
          day_of_week: Number(rest.day_of_week),
          duration_minutes: Number(rest.duration_minutes) || 30,
        })),
      };
      await axios.put(`${API}/timetable/template`, payload, auth());
      toast.success('Timetable saved — will apply to new weeks.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-10">
        <Loader2 className="w-6 h-6 animate-spin text-[#7C6F9C]" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4" data-testid="therapist-timetable-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-serif text-stone-900">Weekly timetable</h1>
          <p className="text-sm text-stone-600 mt-1">
            Set your normal weekly schedule once — it carries over every week
            until you change it. Manual edits to a specific week are preserved.
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="timetable-save-btn" className="self-stretch sm:self-auto">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span className="ml-2">Save</span>
        </Button>
      </div>

      <Card className="p-4 space-y-3">
        {entries.length === 0 && (
          <p className="text-sm text-stone-500">No entries yet. Add one below.</p>
        )}
        {entries.map((e, i) => (
          <div
            key={i}
            className="rounded-xl border border-stone-200 bg-white p-3 space-y-2 sm:space-y-0 sm:grid sm:grid-cols-12 sm:gap-2 sm:items-center sm:p-2 sm:bg-transparent sm:border-0 sm:rounded-none"
            data-testid={`timetable-row-${i}`}
          >
            {/* Row 1 on mobile: Day · Time · Delete */}
            <div className="flex items-center gap-2 sm:contents">
              <label className="sm:col-span-2 flex-1 sm:flex-initial">
                <span className="block text-[10px] uppercase tracking-wide text-stone-500 sm:hidden">Day</span>
                <select
                  className="w-full border rounded-md px-2 py-2 text-sm sm:py-1.5"
                  value={e.day_of_week}
                  onChange={(ev) => updateEntry(i, { day_of_week: Number(ev.target.value) })}
                  data-testid={`timetable-day-${i}`}
                >
                  {DAYS.map((d, idx) => <option key={idx} value={idx}>{d}</option>)}
                </select>
              </label>
              <label className="sm:col-span-2 flex-1 sm:flex-initial">
                <span className="block text-[10px] uppercase tracking-wide text-stone-500 sm:hidden">Time</span>
                <Input
                  type="time"
                  className="w-full"
                  value={e.time || ''}
                  onChange={(ev) => updateEntry(i, { time: ev.target.value })}
                  data-testid={`timetable-time-${i}`}
                />
              </label>
              <button
                onClick={() => removeEntry(i)}
                className="sm:col-span-1 self-end text-rose-600 hover:bg-rose-50 rounded-full p-2 flex-shrink-0"
                data-testid={`timetable-remove-${i}`}
                aria-label="Remove"
                type="button"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Row 2 on mobile: Child select (full width) */}
            <label className="block sm:col-span-4">
              <span className="block text-[10px] uppercase tracking-wide text-stone-500 sm:hidden">Child</span>
              <select
                className="w-full border rounded-md px-2 py-2 text-sm sm:py-1.5"
                value={e.child_id || ''}
                onChange={(ev) => {
                  const c = children.find((x) => x.id === ev.target.value);
                  updateEntry(i, { child_id: ev.target.value, child_name: c?.name || '' });
                }}
                data-testid={`timetable-child-${i}`}
              >
                <option value="">(Free slot / unassigned)</option>
                {children.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>

            {/* Row 3 on mobile: Duration · Notes */}
            <div className="flex items-end gap-2 sm:contents">
              <label className="sm:col-span-1 w-20 sm:w-auto flex-shrink-0">
                <span className="block text-[10px] uppercase tracking-wide text-stone-500 sm:hidden">Mins</span>
                <Input
                  type="number"
                  className="w-full"
                  value={e.duration_minutes || 30}
                  onChange={(ev) => updateEntry(i, { duration_minutes: ev.target.value })}
                  placeholder="mins"
                  data-testid={`timetable-duration-${i}`}
                />
              </label>
              <label className="flex-1 sm:col-span-2">
                <span className="block text-[10px] uppercase tracking-wide text-stone-500 sm:hidden">Notes</span>
                <Input
                  className="w-full"
                  value={e.notes || ''}
                  onChange={(ev) => updateEntry(i, { notes: ev.target.value })}
                  placeholder="Optional"
                />
              </label>
            </div>
          </div>
        ))}
        <Button variant="outline" onClick={addEntry} data-testid="timetable-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Add entry
        </Button>
      </Card>
    </div>
  );
}
